/**
 * MonthDayCell Component
 *
 * Month view cell showing total booked hours for a day.
 * Composes the DayCell atom with hours display.
 * Optionally shows date label and checkbox for selection mode.
 *
 * @example
 * ```tsx
 * <MonthDayCell
 *   hours={8}
 *   date={new Date()}
 *   status="fullyBooked"
 *   selectable
 *   showCheckbox
 *   onClick={handleClick}
 * />
 * ```
 */

import { Checkbox } from "@/components/ui/forms/Checkbox";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { DayCell } from "@/components/schedule/DayCell";
import { generateId } from "@/lib/utils";
import { formatDayCellTooltip } from "@/utils/date";
import { formatDecimal } from "@/utils/number/formatters";
import classNames from "classnames";
import { format } from "date-fns";
import React, { useCallback, useMemo } from "react";
import styles from "./MonthDayCell.module.css";
import type { MonthDayCellProps } from "./types";

export const MonthDayCell: React.FC<MonthDayCellProps> = ({
  hours,
  date,
  status = "free",
  colored = true,
  isToday = false,
  isWeekend = false,
  isRedDay = false,
  selected = false,
  selectable = false,
  inRangePreview = false,
  showCheckbox = false,
  absenceFill = 0,
  disabled = false,
  onClick,
  onMouseEnter,
  className,
  "data-testid": testId,
}) => {
  // Format date label if date is provided
  const dateLabel = date ? format(date, "d.M") : undefined;

  // Build tooltip content
  const tooltipContent = useMemo(() => {
    if (!date) return undefined;
    return formatDayCellTooltip(date) ?? undefined;
  }, [date]);

  // Handle click with stopPropagation to prevent background click
  // Passes the event to onClick for modifier key detection (shift, ctrl, meta)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent background click from closing sidebar
      onClick?.(e);
    },
    [onClick],
  );

  // Cell is "locked" when there's no onClick handler (another employee is in edit mode)
  const isLocked = !onClick;

  const cellContent = (
    <div
      className={classNames(
        styles.container,
        isWeekend && styles.weekend,
        isToday && styles.today,
        selectable && styles.selectable,
        isLocked && styles.locked,
        className,
      )}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      aria-label={tooltipContent}
      data-testid={testId}
      data-locked={isLocked || undefined}
    >
      {/* Date label */}
      {dateLabel && (
        <div
          className={classNames(
            styles.dateLabel,
            isWeekend && styles.weekend,
            isRedDay && styles.redDay,
            isToday && styles.today,
          )}
        >
          {dateLabel}
        </div>
      )}

      {/* Hours display using DayCell */}
      <DayCell
        status={status}
        size="md"
        colored={colored}
        isWeekend={isWeekend}
        selected={selected}
        selectable={selectable}
        inRangePreview={inRangePreview}
        absenceFill={absenceFill}
        disabled={disabled}
        className={classNames(styles.dayCell, styles.fullWidth)}
      >
        <span
          className={classNames(
            styles.hours,
            hours === 0 && styles.hoursHidden,
          )}
        >
          {formatDecimal(hours)}
        </span>
      </DayCell>

      {/* Checkbox for selection mode */}
      {showCheckbox && selectable && (
        <div className={styles.checkboxWrapper}>
          <Checkbox
            id={generateId("month-cell")}
            checked={selected}
            onChange={() => {}}
            size="sm"
          />
        </div>
      )}
    </div>
  );

  // Wrap in tooltip if we have content
  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent} position="top" delay={300}>
        {cellContent}
      </Tooltip>
    );
  }

  return cellContent;
};

MonthDayCell.displayName = "MonthDayCell";

export default MonthDayCell;
